import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { Seo } from "@/lib/seo";
import { EASE, viewportOnce } from "@/lib/motion";
import { IMAGES, PILLARS } from "@/lib/data";
import { SITE } from "@/lib/site";
import { Fade, Mask } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";

export default function About() {
  return (
    <main id="contenu">
      <Seo
        title="À propos — La technique, la créativité, l'exigence | SONOCO EVENTS"
        description="Basée à Fréjus, dans le Var, SONOCO EVENTS accompagne les professionnels de l'événementiel dans leurs projets techniques : sonorisation, éclairage, régie, structures et scénographie. Sud de la France et PACA."
        path="/a-propos"
      />

      <header className="mx-auto max-w-[1600px] px-5 pb-16 pt-32 sm:px-10 sm:pb-24 sm:pt-44 lg:px-16">
        <SectionLabel num="04" name="À propos" />
        <h1 className="mt-10 text-[clamp(2.4rem,7.5vw,7.5rem)] font-extrabold uppercase leading-[0.88] tracking-tightest">
          <Mask>La technique.</Mask>
          <Mask delay={0.1}>La créativité.</Mask>
          <Mask delay={0.2}>L'exigence.</Mask>
        </h1>
      </header>

      <section className="mx-auto max-w-[1600px] px-5 sm:px-10 lg:px-16">
        <div className="grid gap-14 lg:grid-cols-12">
          <Fade className="max-w-2xl space-y-8 lg:col-span-7">
            <p className="text-xl font-light leading-relaxed text-ink sm:text-2xl">
              Basée à Fréjus, dans le Var, SONOCO EVENTS accompagne les professionnels de
              l'événementiel dans leurs projets techniques.
            </p>
            <p className="text-lg leading-relaxed text-mute">
              Notre approche associe maîtrise technique, recherche de solutions et sens du détail.
              Chaque production est pensée en fonction de ses enjeux, de son environnement et de ses
              contraintes opérationnelles.
            </p>
            <p className="text-lg leading-relaxed text-mute">
              Nous intervenons principalement dans le Sud de la France, et étudions vos projets
              ailleurs selon leurs exigences.
            </p>
          </Fade>
          <Fade className="lg:col-span-4 lg:col-start-9" delay={0.15}>
            <div className="overflow-hidden border border-line">
              <motion.img
                src={IMAGES.about}
                alt="Montage d'une structure de scène en extérieur — SONOCO EVENTS, Fréjus (Var)"
                className="aspect-[4/5] w-full object-cover"
                loading="lazy"
                initial={{ scale: 1.1 }}
                whileInView={{ scale: 1 }}
                viewport={viewportOnce}
                transition={{ duration: 1.4, ease: EASE }}
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
              Fréjus — Var, France
            </p>
          </Fade>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-36 lg:px-16">
        <SectionLabel num="01" name="Notre approche" />
        <div className="mt-12">
          {PILLARS.map((p, i) => (
            <Fade key={p.num} delay={i * 0.05} y={20}>
              <div
                data-testid={`about-pillar-${p.num}`}
                className="grid grid-cols-12 items-baseline gap-4 border-t border-line py-6 last:border-b sm:gap-8 sm:py-9"
              >
                <span className="col-span-2 font-mono text-xs text-mute sm:col-span-1">
                  {p.num}
                </span>
                <h3 className="col-span-10 text-2xl font-extrabold uppercase tracking-tight sm:col-span-4 sm:text-5xl">
                  {p.name}
                </h3>
                <p className="col-span-12 text-sm leading-relaxed text-mute sm:col-span-7 sm:text-base">
                  {p.desc}
                </p>
              </div>
            </Fade>
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-panel">
        <div className="mx-auto max-w-[1600px] px-5 py-24 sm:px-10 sm:py-32 lg:px-16">
          <SectionLabel num="02" name="Travailler avec nous" />
          <div className="mt-14 grid gap-12 lg:grid-cols-2">
            <Fade>
              <h2 className="text-[clamp(2.2rem,6vw,5rem)] font-extrabold uppercase leading-[0.9] tracking-tightest">
                Parlons de votre production.
              </h2>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="group mt-10 inline-flex items-center gap-3 border border-ink/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-bg"
              >
                Nous contacter
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Fade>
            <Fade className="space-y-6" delay={0.15}>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="about-email-link"
                className="flex items-center gap-4 border-b border-line pb-5 text-lg text-ink transition-colors duration-300 hover:text-mute"
              >
                <Mail className="h-5 w-5 text-mute" />
                {SITE.email}
              </a>
              {SITE.phones.map((p) => (
                <a
                  key={p}
                  href={`tel:${p.replace(/\s/g, "")}`}
                  data-testid="about-phone-link"
                  className="flex items-center gap-4 border-b border-line pb-5 text-lg text-ink transition-colors duration-300 hover:text-mute"
                >
                  <Phone className="h-5 w-5 text-mute" />
                  {p}
                </a>
              ))}
              <p className="pt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-faint">
                {SITE.location}
                <br />
                Interventions Sud de la France & PACA — projets ailleurs selon exigences
              </p>
            </Fade>
          </div>
        </div>
      </section>
    </main>
  );
}
